/**
 * An instance's `%Name` table, composed as instantiate builds it: the sub-scene's own names first
 * (packed_scene.cpp:565-570), then each host setting at its heading, in file order. The graft is
 * the only composer, and the render path reads the finished table off the merged node.
 */
import { nodeAt, replacedAt } from './liveTreeIndex';
import { joinPath, pathBetween, pathUnder } from '../utils/nodePath';
import { UNIQUE_NODE_PREFIX, nodePathNames, walkFromRoot, walkedPath, } from '../godot/string/nodePath';
import { holderAfterSetting, isOutsideInstance, } from '../parser/nodeOwner';
import { passesContentTrail } from '../parser/idTrail';
/**
 * The instance's table while its graft runs. A set acquires a free name and the first claimant
 * keeps it (node.cpp:2225-2231). A clear releases the node's own name (node.cpp:2236-2249).
 */
export class InstanceNameTable {
    placements;
    /** Written only by `landOne`: each name a setting moved, undefined where it freed the name. */
    changed = new Map();
    own;
    settings;
    nested;
    followTrail;
    /** Written only by `landUpTo`: how many of `settings` have landed. */
    landed = 0;
    /** Written only by `paths`, read once the graft has landed every setting. */
    finished;
    /** Written only by `ownerChosen`: what this graft decided for each hand-off its settings carry. */
    handOffDecisions = [];
    /**
     * @param placements where the graft placed each host child that only it can name, as
     *   `uniqueParentPlacements` keys it, filled in as the graft seats them
     */
    constructor({ own, settings, nested, followTrail }, placements) {
        this.placements = placements;
        this.own = own;
        this.settings = settings;
        this.nested = nested;
        this.followTrail = followTrail;
    }
    /**
     * The sub-path a host child states, walked over `tree` as {@link subPathSegments} walks it, with
     * the table of each nested instance the walk stands on read first.
     */
    walk(subPath, tree) {
        const { nested } = this;
        const nestedTable = nested &&
            ((path) => {
                const node = nodeAt(tree, path.split('/'));
                return node && nested(node);
            });
        return subPathSegments(this.placedTrailSeat(subPath), tree, this.claimed, nestedTable);
    }
    /**
     * `path` with its leading sub-path replaced where a trail placed the node there: a segment of a
     * content trail (`contentTrailSegment`), or a plain path the base lacks. A path through a `%Name`
     * resolves through the claims as they stand when it lands, so a later claim moves it.
     */
    placedTrailSeat(path) {
        if (this.placements.size === 0)
            return path;
        const segments = path.split('/');
        if (!passesContentTrail(segments[0]) && path.includes(UNIQUE_NODE_PREFIX))
            return path;
        return placedBelow(this.placements, segments).join('/');
    }
    /** The path from the sub-scene root that holds `key` now, as in {@link UniqueNamePaths}. */
    claimed = (key) => this.changed.has(key) ? this.changed.get(key) : this.own().get(key);
    /** The finished table: the sub-scene's own, the same object where no setting moved a name. */
    paths() {
        this.finished ??= this.changed.size === 0 ? this.own() : withChanges(this.own(), this.changed);
        return this.finished;
    }
    /** `tree` once every setting written before host child `hostChildrenSeen` has landed. */
    landUpTo(tree, hostChildrenSeen) {
        let landedTree = tree;
        for (; this.landed < this.settings.length; this.landed++) {
            const setting = this.settings[this.landed];
            if (setting.hostChildrenBefore > hostChildrenSeen)
                break;
            landedTree = this.landOne(landedTree, setting);
        }
        return landedTree;
    }
    /**
     * `tree` once `setting` lands in this table, or once it is handed to the nested instance whose
     * table it names. A setting on a node the sub-scene lacks, or one that awaits a hand-off, changes
     * nothing.
     */
    landOne(tree, authored) {
        if (authored.awaits)
            return tree;
        const pathInInstance = this.placedTrailSeat(authored.pathInInstance);
        const placed = pathInInstance === authored.pathInInstance ? authored : { ...authored, pathInInstance };
        const setting = this.ownerChosen(tree, placed);
        if (!setting)
            return tree;
        if (isOutsideInstance(setting)) {
            if (!setting.ownerInInstance) {
                this.land(setting.pathInInstance, setting.isUnique);
                return tree;
            }
            return handOutside(tree, setting.ownerInInstance.split('/'), setting.pathInInstance, setting);
        }
        const segments = settingSegments(tree, setting.pathInInstance, this.claimed);
        if (!segments)
            return tree;
        const owner = tableOwner(tree, segments, setting.ownerInInstance);
        if (owner === null)
            return tree;
        if (owner === OUTSIDE_THE_OWNER) {
            return handOutside(tree, setting.ownerInInstance.split('/'), segments.join('/'), setting);
        }
        if (owner !== THIS_INSTANCE) {
            return handTo(tree, segments.slice(0, owner.nestedAt + 1), rebased(setting, segments, owner.nestedAt));
        }
        this.land(segments.join('/'), setting.isUnique);
        return tree;
    }
    /**
     * `authored` with the owner Godot takes: the one its `owner=` walk finds, or its `owner_uid_path=` owner
     * where that walk finds no node (packed_scene.cpp:159-163). Null where the owner names nothing, where
     * a scene the walk enters is still loading, or where the `owner_uid_path=` owner is in another
     * instance, to which the setting is handed off.
     */
    ownerChosen(tree, authored) {
        const walked = this.ownerWalked(tree, authored);
        // A scene the owner trail enters is still loading, so nothing is decided yet.
        if (walked === OWNER_PENDING)
            return null;
        const { ownerHandOff: handOff } = authored;
        const place = walked === null ? 'absent' : nodePlace(tree, walked.ownerInInstance ?? '');
        if (handOff) {
            // An owner inside a nested instance is that instance's graft to find, which carries the hand-off on.
            if (place === 'nested')
                return walked;
            this.handOffDecisions.push({ handOff, isHandedOff: place === 'absent' });
            return place === 'absent' ? null : walked;
        }
        if (place !== 'absent' || !authored.ownerFallback)
            return walked;
        const fallback = this.ownerWalked(tree, withFallbackOwner(authored));
        return fallback === OWNER_PENDING ? null : fallback;
    }
    ownerWalked(tree, setting) {
        const followed = withOwnerTrailFollowed(setting, this.followTrail);
        if (followed === OWNER_PENDING)
            return followed;
        return followed && withOwnerWalked(tree, followed, this.claimed);
    }
    /** The table once the node at `path` sets or clears its name. */
    land(path, isUnique) {
        const key = UNIQUE_NODE_PREFIX + nodePathNames(path).at(-1);
        const holder = this.claimed(key);
        const next = holderAfterSetting(holder, path, isUnique);
        // A set on a held name or a clear on a free one changes nothing, so `paths` keeps the own table.
        if (next !== holder)
            this.changed.set(key, next);
    }
}
/** `paths` with each changed name moved to its new holder, or dropped where a clear freed it. */
function withChanges(paths, changed) {
    const moved = new Map(paths);
    for (const [key, path] of changed) {
        if (path === undefined)
            moved.delete(key);
        else
            moved.set(key, path);
    }
    return moved;
}
/** `setting` with the owner its `owner_uid_path=` names, which Godot takes where the `owner=` walk finds nothing. */
function withFallbackOwner({ ownerFallback, ...rest }) {
    return { ...rest, ownerContentTrail: undefined, ...ownerFallback };
}
/** A walk to an owner through a scene not loaded yet, which the next collapse follows again. */
const OWNER_PENDING = 'owner pending';
/**
 * `setting` with the owner its `ownerContentTrail` finds below the instance, as the loader's lookup
 * finds it (packed_scene.cpp:159-163). Null where no heading answers the trail, so the node has no
 * owner, and {@link OWNER_PENDING} where a scene it enters is not loaded yet.
 */
function withOwnerTrailFollowed(setting, followTrail) {
    if (setting.ownerContentTrail === undefined)
        return setting;
    const walk = followTrail(setting.ownerContentTrail);
    if (walk.status === 'pending')
        return OWNER_PENDING;
    if (walk.status !== 'found')
        return null;
    const { ownerContentTrail: _followed, ...rest } = setting;
    return { ...rest, ownerInInstance: pathUnder(setting.ownerInInstance ?? '', walk.path) };
}
/**
 * `setting` with each `%Name` in its explicit owner replaced through this table, as the loader's
 * walk from the root reads it (packed_scene.cpp:159-161), or null where the owner names nothing.
 */
function withOwnerWalked(tree, setting, claimed) {
    const { ownerInInstance } = setting;
    if (!ownerInInstance)
        return setting;
    const segments = subPathSegments(ownerInInstance, tree, claimed);
    return segments && { ...setting, ownerInInstance: segments.join('/') };
}
/** The node a setting names, its parent walked as a host sub-path, or null where that names nothing. */
function settingSegments(tree, pathInInstance, claimed) {
    const authored = pathInInstance.split('/');
    const parent = authored.length > 1 ? subPathSegments(authored.slice(0, -1).join('/'), tree, claimed) : [];
    return parent && [...parent, authored.at(-1)];
}
const THIS_INSTANCE = 'this instance';
const OUTSIDE_THE_OWNER = 'outside the owner';
/**
 * Whose table a setting at `segments` lands in, or null for a node the sub-scene lacks or a table
 * no instance reads. Without an explicit owner, a node inside a nested instance keeps that
 * instance's owner (resource_format_text.cpp:264-265).
 */
function tableOwner(tree, segments, ownerInInstance) {
    const nestedAt = instanceDepth(tree, segments.slice(0, -1));
    if (nestedAt === null || !namesNodeBelow(tree, segments, nestedAt))
        return null;
    if (ownerInInstance === '')
        return THIS_INSTANCE;
    if (ownerInInstance === undefined)
        return nestedAt === undefined ? THIS_INSTANCE : { nestedAt };
    const ownerSegments = ownerInInstance.split('/');
    const ownerAt = instanceDepth(tree, ownerSegments);
    if (typeof ownerAt !== 'number')
        return null;
    return ownerSegments.every((name, index) => segments[index] === name)
        ? { nestedAt: ownerAt }
        : OUTSIDE_THE_OWNER;
}
/**
 * The depth of the first instance node along `path`: undefined where none is one, and null where
 * `path` names nothing before one.
 */
function instanceDepth(tree, path) {
    let siblings = tree;
    for (const [depth, name] of path.entries()) {
        const found = siblings.find((n) => n.name === name);
        if (!found)
            return null;
        if (found.instance)
            return depth;
        siblings = found.children;
    }
    return undefined;
}
/**
 * `tree` with `setting`, on the node at `nodePath` outside the nested instance that holds `owner`,
 * handed to that instance, whose graft walks the rest of `owner` and lands it in its own table. An
 * owner no nested instance holds is no table this graft reaches.
 */
function handOutside(tree, owner, nodePath, setting) {
    const nestedAt = instanceDepth(tree, owner);
    if (typeof nestedAt !== 'number')
        return tree;
    const instancePath = owner.slice(0, nestedAt + 1);
    const { isUnique, hostChildrenBefore, ownerHandOff } = setting;
    return handTo(tree, instancePath, {
        pathInInstance: pathBetween(instancePath.join('/'), nodePath),
        isUnique,
        hostChildrenBefore,
        ...(nestedAt < owner.length - 1 ? { ownerInInstance: owner.slice(nestedAt + 1).join('/') } : {}),
        ...(ownerHandOff ? { ownerHandOff } : {}),
    });
}
/**
 * Where the sub-path's node is: `here` in `tree`, `nested` in a nested instance, which that
 * instance's graft finds, or `absent`. A sub-path that climbs out counts as here, as it names a
 * node above, which no tree here can check.
 */
function nodePlace(tree, subPath) {
    if (subPath === '' || subPath.startsWith(OUTSIDE))
        return 'here';
    const segments = subPath.split('/');
    const nestedAt = instanceDepth(tree, segments.slice(0, -1));
    if (nestedAt === null)
        return 'absent';
    if (nestedAt !== undefined)
        return 'nested';
    return nodeAt(tree, segments) === undefined ? 'absent' : 'here';
}
/** Whether `segments` names a node of `tree`, or lies in the nested instance at `nestedAt` (`instanceDepth` of its parent path). */
function namesNodeBelow(tree, segments, nestedAt) {
    return nestedAt !== undefined || nodeAt(tree, segments) !== undefined;
}
/**
 * `path`, as segments, with its longest leading sub-path that `placements` holds replaced by the
 * placed path.
 *
 * @canonical A path with its leading sub-path replaced where a graft placed it
 */
export function placedBelow(placements, path) {
    for (let end = path.length; end > 0; end--) {
        const placed = placements.get(path.slice(0, end).join('/'));
        if (placed !== undefined)
            return [...placed.split('/'), ...path.slice(end)];
    }
    return [...path];
}
/** `setting` spelt below the nested instance at depth `at` of `segments`, its expanded path. */
function rebased(setting, segments, at) {
    const pathInInstance = segments.slice(at + 1).join('/');
    const { ownerInInstance } = setting;
    if (ownerInInstance === undefined)
        return { ...setting, pathInInstance };
    return {
        ...setting,
        pathInInstance,
        ownerInInstance: ownerInInstance
            .split('/')
            .slice(at + 1)
            .join('/'),
    };
}
/**
 * `tree` with `setting` appended to the nested instance at `at`. It lands after the children that
 * instance holds now, as file order places it for that instance's graft.
 */
function handTo(tree, at, setting) {
    return replacedAt(tree, at, (target) => ({
        ...target,
        instanceNameSettings: [
            ...(target.instanceNameSettings ?? []),
            { ...setting, hostChildrenBefore: target.children.length },
        ],
    }));
}
/**
 * A sub-path below the sub-scene root, walked as Godot walks a `parent=` path (`walkFromRoot`) over
 * the instance's `%Name` table, or null where it names nothing. A walk into a nested instance
 * keeps the rest as written, for that instance's own graft to resolve. A sub-path with no `%Name`
 * is the path as written, which the seating already walked.
 */
export function subPathSegments(subPath, tree, claimed, nested) {
    const segments = subPath.split('/');
    if (!segments.some((segment) => segment.startsWith(UNIQUE_NODE_PREFIX)))
        return segments;
    const path = walkedPath(walkFromRoot(subPath, subSceneWalkTables(tree, claimed, nested)));
    if (path === null)
        return null;
    return path === '' ? [] : path.split('/');
}
/** The first segment of a walk that left a tree for the one above it. */
export const OUTSIDE = '..';
/**
 * The end of a walk that found nothing in a tree whose root is an instance root. A `%Name` first
 * step that the root's table lacks reads the table of the root's owner next (node.cpp:1930-1938),
 * which the tree above holds. Null for any other failure: nothing above answers it.
 */
export function endAboveRoot(subPath, claimed) {
    const [first = ''] = subPath.split('/');
    const isLacking = first.startsWith(UNIQUE_NODE_PREFIX) && claimed(first) === undefined;
    return isLacking ? joinPath(OUTSIDE, subPath) : null;
}
const INSIDE_INSTANCE = 'inside a nested instance';
/** The walk's tables over the sub-scene: a nested instance is opaque, and so is anything below it. */
function subSceneWalkTables(tree, claimed, nested) {
    const reach = (path) => {
        if (path === '')
            return undefined;
        const segments = path.split('/');
        const nestedAt = instanceDepth(tree, segments.slice(0, -1));
        if (nestedAt === null)
            return undefined;
        return nestedAt === undefined ? nodeAt(tree, segments) : INSIDE_INSTANCE;
    };
    return {
        exists: (path) => path === '' || reach(path) !== undefined,
        isOpaque: (path) => {
            const at = reach(path);
            return at === INSIDE_INSTANCE || Boolean(at?.instance);
        },
        uniquePaths: { get: claimed },
        // Only an instance root has a table of its own: a node below it reads that instance's.
        instanceTable: (path) => {
            const at = reach(path);
            return at === INSIDE_INSTANCE || !at?.instance ? undefined : nested?.(path);
        },
    };
}
//# sourceMappingURL=instanceNameTable.js.map